import { useRef } from 'react';
import { useStore } from '../store';
import {
  X,
  Printer,
  FileText,
  File,
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExportPDF: () => void;
  onExportDOCX: () => void;
}

export default function DocumentPreviewModal({ isOpen, onClose, onExportPDF, onExportDOCX }: Props) {
  const { sections, currentSessionName, genericSections } = useStore();
  const printRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const primaryParty = genericSections.find((s) => s.id === 'primary');
  const secondaryParty = genericSections.find((s) => s.id === 'secondary');
  const documentDetails = genericSections.find((s) => s.id === 'document');

  const getFieldValue = (section: typeof primaryParty, fieldId: string) => {
    return section?.fields.find((f) => f.id === fieldId)?.value || '';
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${currentSessionName}</title>
        <style>
          body {
            font-family: 'Times New Roman', Times, serif;
            max-width: 8.5in;
            margin: 1in auto;
            padding: 0;
            font-size: 12pt;
            line-height: 1.6;
            color: #1a1a1a;
          }
          .header {
            text-align: center;
            margin-bottom: 2em;
            border-bottom: 2px solid #333;
            padding-bottom: 1em;
          }
          .header h1 {
            font-size: 18pt;
            margin: 0 0 0.5em 0;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .parties {
            margin-bottom: 2em;
          }
          .party {
            margin-bottom: 1em;
          }
          .party-label {
            font-weight: bold;
            font-size: 10pt;
            text-transform: uppercase;
            color: #555;
          }
          .section {
            margin-bottom: 1.5em;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 14pt;
            font-weight: bold;
            margin-bottom: 0.5em;
            color: #333;
            border-bottom: 1px solid #ddd;
            padding-bottom: 0.25em;
          }
          .section-content {
            text-align: justify;
            white-space: pre-wrap;
          }
          .reference {
            font-size: 10pt;
            color: #666;
            margin-top: 0.5em;
          }
          @media print {
            body { margin: 0.75in; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold">Document Preview</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="btn btn-secondary flex items-center gap-2 text-sm"
              title="Print"
            >
              <Printer size={16} />
              Print
            </button>
            <button
              onClick={onExportPDF}
              className="btn btn-secondary flex items-center gap-2 text-sm"
              title="Export PDF"
            >
              <FileText size={16} />
              PDF
            </button>
            <button
              onClick={onExportDOCX}
              className="btn btn-secondary flex items-center gap-2 text-sm"
              title="Export DOCX"
            >
              <File size={16} />
              DOCX
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
          <div
            ref={printRef}
            className="bg-white max-w-3xl mx-auto shadow-sm border border-gray-200 p-12"
            style={{ fontFamily: "'Times New Roman', Times, serif" }}
          >
            {/* Document Header */}
            <div className="header text-center mb-8 border-b-2 border-gray-800 pb-4">
              <h1 className="text-xl font-bold uppercase tracking-wider mb-1">
                {currentSessionName}
              </h1>
              <p className="reference text-sm text-gray-500">
                {getFieldValue(documentDetails, 'reference_number') &&
                  `Reference: ${getFieldValue(documentDetails, 'reference_number')} | `}
                Date: {currentDate}
              </p>
            </div>

            {/* Parties Information */}
            {(getFieldValue(primaryParty, 'primary_name') ||
              getFieldValue(secondaryParty, 'secondary_name')) && (
              <div className="parties mb-8">
                {getFieldValue(primaryParty, 'primary_name') && (
                  <div className="party mb-3">
                    <p className="party-label text-xs uppercase text-gray-500 font-semibold tracking-wide">
                      {primaryParty?.title || 'Primary Party'}
                    </p>
                    <p className="font-semibold">
                      {getFieldValue(primaryParty, 'primary_name')}
                    </p>
                    {getFieldValue(primaryParty, 'primary_role') && (
                      <p className="text-sm text-gray-600">
                        {getFieldValue(primaryParty, 'primary_role')}
                      </p>
                    )}
                    {getFieldValue(primaryParty, 'primary_location') && (
                      <p className="text-sm text-gray-600">
                        {getFieldValue(primaryParty, 'primary_location')}
                      </p>
                    )}
                  </div>
                )}

                {getFieldValue(secondaryParty, 'secondary_name') && (
                  <div className="party mb-3">
                    <p className="party-label text-xs uppercase text-gray-500 font-semibold tracking-wide">
                      {secondaryParty?.title || 'Secondary Party'}
                    </p>
                    <p className="font-semibold">
                      {getFieldValue(secondaryParty, 'secondary_name')}
                    </p>
                    {getFieldValue(secondaryParty, 'secondary_address') && (
                      <p className="text-sm text-gray-600 whitespace-pre-line">
                        {getFieldValue(secondaryParty, 'secondary_address')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Sections */}
            {sections.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText size={48} className="mx-auto mb-3 opacity-50" />
                <p>No content to preview</p>
                <p className="text-sm mt-1">Generate content in your sections first.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {sections.map((section) => (
                  <div key={section.id} className="section">
                    <h2 className="section-title text-base font-bold text-gray-800 border-b border-gray-200 pb-1 mb-3">
                      {section.title}
                    </h2>
                    <div className="section-content text-sm leading-relaxed text-gray-700 whitespace-pre-wrap text-justify">
                      {section.content || (
                        <span className="italic text-gray-400">No content generated yet.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            {getFieldValue(documentDetails, 'prepared_by') && (
              <div className="mt-12 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Prepared by: {getFieldValue(documentDetails, 'prepared_by')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
